import moment from 'moment'

import { Collection } from 'modules/core/lib/mobx-rest'
import { apiClient } from 'modules/core/lib/mobx-rest'

import Document from 'modules/documents/store/models/Document'
import type Employee from 'modules/employees/store/models/Employee'

interface CreateFromOptions {
  file: File
  employee?: Employee
  isPublic?: boolean
  notify?: boolean
  esignature?: boolean
  folderId: number | null | undefined
  authorId: number
}

export interface BulkUpdateOptions {
  document_id: number
  employee_id: number
  signees: number[]
  request_esignature: boolean
  public: boolean
  is_pending_assignment: boolean
  distribution_method: string
}
interface BulkDestroyOptions {
  document_ids: number[]
}

interface MoveToTrashBinOptions {
  document_ids: number[]
}

class Documents extends Collection<Document> {
  get cacheKey(): string {
    return 'documents'
  }

  get indexes() {
    return ['employee_id', 'folder_id']
  }

  model() {
    return Document
  }

  createFrom(options: CreateFromOptions) {
    return this.create(this._buildData(options))
  }

  bulkCreate(data: Record<string, unknown>) {
    return this.rpc('bulk_create', data)
  }

  bulkUpdate(data: BulkUpdateOptions[]) {
    return this.rpc('bulk_update', {
      documents: data,
    })
  }

  bulkDestroy(data: BulkDestroyOptions) {
    return this.rpc('bulk_destroy', data)
  }

  assignedBulkDestroy(data: BulkDestroyOptions) {
    return this.rpc('assigned_bulk_destroy', data)
  }

  moveToTrashBin(data: MoveToTrashBinOptions): Promise<void> {
    return this.rpc('move_to_trash_bin', data)
  }

  download(ids: number[]) {
    return this.rpc('download_documents', { ids: `${ids.join(',')}` }, { responseType: 'blob' })
  }

  async notifierFileUrl(id: number, recipient_id: number): Promise<string | null> {
    const { url } = await apiClient()
      .get(`${this.model().staticUrl()}/notifier_file_url`, {
        id: id,
        recipient_id: recipient_id,
      })
      .catch(() => {
        return { url: null }
      })

    return url
  }

  _buildData({
    file,
    isPublic,
    employee,
    notify,
    esignature,
    folderId,
    authorId,
  }: CreateFromOptions) {
    return {
      file,
      author_id: authorId,
      content_type: file.type,
      created_at: moment().unix(),
      employee_id: employee ? employee.attributes.id : null,
      filename: file.name,
      notify: notify === undefined ? true : notify,
      public: isPublic === undefined ? true : isPublic,
      request_esignature: esignature ?? false,
      folder_id: folderId,
    }
  }
}

const singleton: Documents = new Documents()

export { Documents }

export default singleton
